import React, { useState, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useLanguage } from '../hooks/useLanguage';
export function ServicesSection() {
  const carouselRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const {
    ref: revealRef,
    isVisible
  } = useScrollReveal();
  const {
    t,
    direction
  } = useLanguage();
  const services = [{
    title: t('services.cardIssuing.title'),
    description: t('services.cardIssuing.description'),
    icon: 'https://cdn.magicpatterns.com/uploads/p9wNgXqTf5s865qgqSB29i/card.svg'
  }, {
    title: t('services.atmAcquiring.title'),
    description: t('services.atmAcquiring.description'),
    icon: 'https://cdn.magicpatterns.com/uploads/jegNccbhUz8TByzbmLGE3q/atm.svg'
  }, {
    title: t('services.mobilePayments.title'),
    description: t('services.mobilePayments.description'),
    icon: 'https://cdn.magicpatterns.com/uploads/jXad5oEVGh1pDvZH3TPpsH/mobile.svg'
  }, {
    title: t('services.merchantAcquiring.title'),
    description: t('services.merchantAcquiring.description'),
    icon: 'https://cdn.magicpatterns.com/uploads/aLZSCF4LuiiMZtZnqW9PRT/merchant.svg'
  }, {
    title: t('services.consultancy.title'),
    description: t('services.consultancy.description'),
    icon: 'https://cdn.magicpatterns.com/uploads/cijg3MPrHor5PMSStSdd8t/consultancy.svg'
  }];
  const scroll = (dir: 'left' | 'right') => {
    if (carouselRef.current) {
      const scrollAmount = carouselRef.current.offsetWidth * 0.3;
      // In RTL, scroll direction is inverted for visual consistency
      const multiplier = direction === 'rtl' ? -1 : 1;
      const scrollValue = dir === 'left' ? -scrollAmount : scrollAmount;
      carouselRef.current.scrollBy({
        left: scrollValue * multiplier,
        behavior: 'smooth'
      });
    }
  };
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!carouselRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - carouselRef.current.offsetLeft);
    setScrollLeft(carouselRef.current.scrollLeft);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !carouselRef.current) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    carouselRef.current.scrollLeft = scrollLeft - walk;
  };
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  return <section className="py-32 md:py-44 text-white relative overflow-hidden" style={{
    background: 'linear-gradient(to bottom, #061D76, #25B8D6)'
  }} dir={direction} data-id="element-213">
      {/* Background Curves */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-[#1d8bba]/30 via-transparent to-transparent" data-id="element-214"></div>

      {/* Bottom-left SVG decoration */}
      <img src="https://cdn.magicpatterns.com/uploads/6b9oFepPZ3EmycFXG1ZrBP/4.svg" alt="" className="absolute bottom-0 left-0 w-[1000px] h-auto pointer-events-none opacity-30" data-id="element-215" />

      <div className="relative z-10" ref={revealRef} data-id="element-216">
        {/* Header with navigation */}
        <div className="container mx-auto px-6 md:px-12 mb-12 flex items-center justify-between" data-id="element-217">
          <span className={`text-white font-bold block reveal-hidden ${isVisible ? 'animate-fade-up' : ''}`} style={{
          fontSize: '1.6rem'
        }} data-id="element-218">
            {t('services.title')}
          </span>

          {/* Navigation arrows */}
          <div className={`flex gap-2 reveal-hidden ${isVisible ? 'animate-fade-up delay-200' : ''}`} dir="ltr" // Keep arrows LTR always
        data-id="element-219">
            <button onClick={() => scroll('left')} className="p-2 border border-white/20 rounded hover:bg-white/10 transition-colors" aria-label="Scroll left" data-id="element-220">
              <ChevronLeftIcon className="w-5 h-5" data-id="element-221" />
            </button>
            <button onClick={() => scroll('right')} className="p-2 border border-white/20 rounded hover:bg-white/10 transition-colors" aria-label="Scroll right" data-id="element-222">
              <ChevronRightIcon className="w-5 h-5" data-id="element-223" />
            </button>
          </div>
        </div>

        {/* Carousel container */}
        <div ref={carouselRef} className={`flex gap-0 overflow-x-auto ${direction === 'rtl' ? 'pr-6 md:pr-12' : 'pl-6 md:pl-12'} scrollbar-hide ${isDragging ? 'cursor-grabbing select-none' : 'cursor-grab'}`} style={{
        WebkitOverflowScrolling: 'touch'
      }} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} data-id="element-224">
          {services.map((service, index) => <div key={index} className="group flex-shrink-0 py-10 px-6 md:px-8 border-l border-t border-b border-white/10 hover:bg-[#25B8D6] transition-colors duration-300 cursor-pointer flex flex-col items-center justify-center text-center aspect-square" style={{
          width: 'calc((100vw - 48px) / 3.5)',
          minWidth: '240px'
        }} data-id="element-225">
              <img src={service.icon} alt="" className="w-10 h-10 mb-6 opacity-80 group-hover:opacity-100 transition-opacity" data-id="element-226" />
              <h3 className="text-lg md:text-xl font-bold mb-3" data-id="element-227">
                {service.title}
              </h3>
              <p className="text-white leading-relaxed text-sm" data-id="element-228">
                {service.description}
              </p>
            </div>)}
          {/* Spacer for last card border */}
          <div className="flex-shrink-0 border-l border-white/10 w-px" data-id="element-229"></div>
        </div>
      </div>
    </section>;
}